import { useCallback, useEffect, useRef, useState } from "react";
import { getContext, getLatestRun } from "./aiCompetitorAnalysisApi";

/**
 * Loads the setup prefill and the company's most recent run.
 *
 * @returns {{context: object|null, latestRun: object|null, loading: boolean,
 *   error: string|null, reload: function}} Page-level data with a retry.
 *
 * @remarks
 * Both requests are issued together rather than in sequence — neither depends on
 * the other, and the page cannot render without both. A fetch id guards against
 * a slow earlier response landing after a newer one and overwriting it.
 */
export function useCompetitorAnalysisData() {
  const [context, setContext] = useState(null);
  const [latestRun, setLatestRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchId = useRef(0);

  const load = useCallback(async () => {
    const id = fetchId.current + 1;
    fetchId.current = id;
    setLoading(true);
    setError(null);

    const [contextResult, runResult] = await Promise.all([
      getContext(),
      getLatestRun(),
    ]);

    if (fetchId.current !== id) return;

    if (!contextResult.isSuccess) {
      setError(
        contextResult.message ||
          "Could not load your company details. Please try again.",
      );
      setLoading(false);
      return;
    }

    setContext(contextResult.data);
    setLatestRun(runResult.isSuccess ? runResult.data || null : null);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { context, latestRun, loading, error, reload: load };
}
